import React, { useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { API_BASE_URL } from '../App';
import Cookies from 'js-cookie';
import { isVirginiaTributeFamily } from '../lib/virginiaTribute';
import { InviteFriendsContent } from './InviteFriendsModal';

const AccountPage = ({ user, setUser, onSettingsSaved, authStatus, product }) => {
  const isCostco = product === 'costco';
  const [playMode, setPlayMode] = useState(user?.playMode || 'monthly');
  const [tnPlayMode, setTnPlayMode] = useState(user?.tnPlayMode || 'monthly');
  const [tnPlatesEnabled, setTnPlatesEnabled] = useState(Boolean(user?.tnPlatesEnabled));
  const [removalPreference, setRemovalPreference] = useState(user?.removalPreference || 'always_ask');
  const [engagementEmailsEnabled, setEngagementEmailsEnabled] = useState(
    user?.engagementEmailsEnabled !== false
  );
  const [shareEngagementNudgeEnabled, setShareEngagementNudgeEnabled] = useState(
    user?.shareEngagementNudgeEnabled !== false
  );
  const [showVirginiaTribute, setShowVirginiaTribute] = useState(
    isVirginiaTributeFamily(user) ? user?.showVirginiaTribute !== false : false
  );
  const [plateArtMap, setPlateArtMap] = useState(Boolean(user?.plateArtMap));
  const familyTributeAccess = isVirginiaTributeFamily(user);
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setPlayMode(user.playMode || 'monthly');
      setTnPlayMode(user.tnPlayMode || 'monthly');
      setTnPlatesEnabled(Boolean(user.tnPlatesEnabled));
      setRemovalPreference(user.removalPreference || 'always_ask');
      setEngagementEmailsEnabled(user.engagementEmailsEnabled !== false);
      setShareEngagementNudgeEnabled(user.shareEngagementNudgeEnabled !== false);
      setShowVirginiaTribute(
        isVirginiaTributeFamily(user) ? user.showVirginiaTribute !== false : false
      );
      setPlateArtMap(Boolean(user.plateArtMap));
    }
  }, [user]);

  if (authStatus === 'pending') {
    return (
      <div className="account-page account-page--loading">
        <div className="account-page__loading-card">
          <p className="account-page__loading-msg">Checking your session…</p>
        </div>
      </div>
    );
  }

  if (authStatus === 'guest' || !user) {
    return <Navigate to="/login" replace />;
  }

  const save = async () => {
    setSaving(true);
    setStatus(null);
    try {
      const res = await fetch(`${API_BASE_URL}/user/settings`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: Cookies.get('session'),
        },
        body: JSON.stringify(
          isCostco
            ? { engagementEmailsEnabled }
            : {
                playMode,
                tnPlayMode,
                tnPlatesEnabled,
                removalPreference,
                engagementEmailsEnabled,
                shareEngagementNudgeEnabled,
                plateArtMap,
                ...(familyTributeAccess ? { showVirginiaTribute } : {}),
              },
        ),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setStatus({ type: 'error', message: data.error || res.statusText });
        return;
      }
      if (data.user) {
        setUser(data.user);
      }
      setStatus({ type: 'ok', message: 'Settings saved.' });
      if (onSettingsSaved) onSettingsSaved();
    } catch (e) {
      setStatus({ type: 'error', message: e.message || 'Network error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`account-page${isCostco ? ' account-page--costco' : ''}`}>
      <div className="account-page__wrap">
        <header className="account-page__header">
          <h1 className="account-page__title">Account</h1>
          <p className="account-page__email">
            Signed in as <strong>{user?.email}</strong>
          </p>
        </header>

        {!isCostco && (
        <>
        <fieldset className="account-page__panel account-page__panel--play">
          <legend className="account-page__legend">Play mode</legend>
          <p className="account-page__hint">
            Monthly is the classic Plate Chase mode. Annual and decennial modes show plates you have seen earlier in
            the year or decade in a different color on the map.
          </p>
          <div className="account-page__options">
            <label className="account-page__radio-row">
              <input
                type="radio"
                name="playMode"
                value="monthly"
                checked={playMode === 'monthly'}
                onChange={() => setPlayMode('monthly')}
              />
              <span className="account-page__radio-text">Monthly — current calendar month only</span>
            </label>
            <label className="account-page__radio-row">
              <input
                type="radio"
                name="playMode"
                value="annual"
                checked={playMode === 'annual'}
                onChange={() => setPlayMode('annual')}
              />
              <span className="account-page__radio-text">Annual — this calendar year (US Eastern)</span>
            </label>
            <label className="account-page__radio-row">
              <input
                type="radio"
                name="playMode"
                value="decennial"
                checked={playMode === 'decennial'}
                onChange={() => setPlayMode('decennial')}
              />
              <span className="account-page__radio-text">Decennial — current calendar decade (e.g. 2020–2029)</span>
            </label>
          </div>
        </fieldset>

        <fieldset className="account-page__panel account-page__panel--tn-play">
          <legend className="account-page__legend">Tennessee</legend>
          <p className="account-page__hint">
            Optional mini-game for Tennessee specialty designs. Off by default — it is not part of the North America
            map. Save this setting to add a Tennessee link to the menu.
          </p>
          <label className="account-page__check-row">
            <input
              type="checkbox"
              checked={tnPlatesEnabled}
              onChange={(e) => setTnPlatesEnabled(e.target.checked)}
            />
            <span className="account-page__check-text">Enable Tennessee specialty plates</span>
          </label>
          {tnPlatesEnabled && (
            <div className="account-page__options">
              <p className="account-page__hint">
                How long a Tennessee sighting counts on <Link to="/tennessee">Tennessee</Link>. This does not
                change your US / Canada / Mexico progress.
              </p>
              <label className="account-page__radio-row">
                <input
                  type="radio"
                  name="tnPlayMode"
                  value="monthly"
                  checked={tnPlayMode === 'monthly'}
                  onChange={() => setTnPlayMode('monthly')}
                />
                <span className="account-page__radio-text">Monthly — current calendar month only</span>
              </label>
              <label className="account-page__radio-row">
                <input
                  type="radio"
                  name="tnPlayMode"
                  value="annual"
                  checked={tnPlayMode === 'annual'}
                  onChange={() => setTnPlayMode('annual')}
                />
                <span className="account-page__radio-text">Annual — this calendar year (US Eastern)</span>
              </label>
              <label className="account-page__radio-row">
                <input
                  type="radio"
                  name="tnPlayMode"
                  value="decennial"
                  checked={tnPlayMode === 'decennial'}
                  onChange={() => setTnPlayMode('decennial')}
                />
                <span className="account-page__radio-text">Decennial — current calendar decade (e.g. 2020–2029)</span>
              </label>
            </div>
          )}
        </fieldset>

        <fieldset className="account-page__panel account-page__panel--remove">
          <legend className="account-page__legend">Removing plates</legend>
          <div className="account-page__options">
            <label className="account-page__radio-row">
              <input
                type="radio"
                name="removalPreference"
                value="always_ask"
                checked={removalPreference === 'always_ask'}
                onChange={() => setRemovalPreference('always_ask')}
              />
              <span className="account-page__radio-text">
                Always ask which month to remove (when using annual/decennial)
              </span>
            </label>
            <label className="account-page__radio-row">
              <input
                type="radio"
                name="removalPreference"
                value="current_month_only"
                checked={removalPreference === 'current_month_only'}
                onChange={() => setRemovalPreference('current_month_only')}
              />
              <span className="account-page__radio-text">
                Only allow removing plates from the <strong>current</strong> month (orange “older” plates cannot be
                removed from the map)
              </span>
            </label>
          </div>
        </fieldset>

        <fieldset className="account-page__panel account-page__panel--plate-art">
          <legend className="account-page__legend">License plate map</legend>
          <p className="account-page__hint">
            On unless you turn it off. Each US state and D.C. you have logged is filled with that
            place&apos;s standard license plate, scaled to fit the state, plus the usual orange or green progress
            color as a wash and a thick outline. Places you have not seen stay plain gray, with no plate.
            Canada and Mexico keep the usual colors. If the Virginia memorial is on, that portrait stays on top
            of the plate. The state buttons under the map stay the solid progress colors so the names stay easy to read.
          </p>
          <label className="account-page__check-row">
            <input
              type="checkbox"
              checked={plateArtMap}
              onChange={(e) => setPlateArtMap(e.target.checked)}
            />
            <span className="account-page__check-text">
              Fill seen states with their license plates
            </span>
          </label>
        </fieldset>

        {familyTributeAccess && (
          <fieldset className="account-page__panel account-page__panel--virginia-tribute">
            <legend className="account-page__legend">Virginia tribute</legend>
            <p className="account-page__hint">
              When enabled, Virginia and West Virginia on the US map each show a memorial portrait of Virginia
              Treacy Combs (1992–2026), with the same gray, orange, and green progress colors as the rest of the
              map. West Virginia uses a photo from her time out west. Only visible to your family on Plate Chase.
            </p>
            <label className="account-page__check-row">
              <input
                type="checkbox"
                checked={showVirginiaTribute}
                onChange={(e) => setShowVirginiaTribute(e.target.checked)}
              />
              <span className="account-page__check-text">
                Show Virginia memorial on Virginia and West Virginia
              </span>
            </label>
          </fieldset>
        )}
        </>
        )}

        <fieldset className="account-page__panel account-page__panel--email">
          <legend className="account-page__legend">Email</legend>
          <p className="account-page__hint">
            {isCostco
              ? 'Occasional reminders if you have not logged a warehouse visit in a while. Unsubscribe anytime from the link in any email.'
              : 'Occasional reminders if you have not logged a plate in a long time. You can also unsubscribe from those messages using the link at the bottom of any re-engagement email.'}
          </p>
          <label className="account-page__check-row">
            <input
              type="checkbox"
              checked={engagementEmailsEnabled}
              onChange={(e) => setEngagementEmailsEnabled(e.target.checked)}
            />
            <span className="account-page__check-text">Send me re-engagement emails when I have been away a while</span>
          </label>
        </fieldset>

        {!isCostco && (
        <fieldset className="account-page__panel account-page__panel--invite">
          <legend className="account-page__legend">Invite friends</legend>
          <p className="account-page__hint">
            When you log enough plates, we may occasionally ask if you&apos;d like to share PlateChase with
            friends. You can turn those prompts off here anytime.
          </p>
          <label className="account-page__check-row">
            <input
              type="checkbox"
              checked={shareEngagementNudgeEnabled}
              onChange={(e) => setShareEngagementNudgeEnabled(e.target.checked)}
            />
            <span className="account-page__check-text">
              Show invite prompts when I hit plate milestones (10 plates, then every 50 after that)
            </span>
          </label>
          <InviteFriendsContent />
        </fieldset>
        )}

        <div className="account-page__actions">
          <button type="button" className="account-page__save" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save settings'}
          </button>
        </div>

        {status?.type === 'ok' && (
          <p className="account-page__status account-page__status--ok" role="status">
            {status.message}
          </p>
        )}
        {status?.type === 'error' && (
          <p className="account-page__status account-page__status--err" role="alert">
            {status.message}
          </p>
        )}
      </div>
    </div>
  );
};

export default AccountPage;
